import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import TOTPSetup from '../components/TOTPSetup';
import SMSMFASetup from '../components/SMSMFASetup';
import { featureFlags } from '../utils/featureFlags';

type MFAMethod = 'totp' | 'sms' | null;

const SetupMFA: React.FC = () => {
  const navigate = useNavigate();
  const smsEnabled = featureFlags.isEnabled('enableSmsMfa');
  const [activeStep, setActiveStep] = useState(smsEnabled ? 0 : 1);
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod>(smsEnabled ? null : 'totp');

  const steps = ['Choose MFA Method', 'Setup & Verify'];

  const handleMethodSelect = (method: MFAMethod) => {
    setSelectedMethod(method);
    setActiveStep(1);
  };

  const handleSetupComplete = () => {
    navigate('/');
  };

  const handleSetupCancel = () => {
    setSelectedMethod(null);
    setActiveStep(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Secure Your Account
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          Multi-factor authentication is required to protect your catalog. Choose your
          preferred authentication method below.
        </Alert>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Choose Your MFA Method
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {/* TOTP Option */}
              <Grid item xs={12} md={4}>
                <Card
                  role="button"
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => handleMethodSelect('totp')}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <PhoneAndroidIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Authenticator App
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Use Google Authenticator, Authy, or similar
                    </Typography>
                    <Chip label="Recommended" color="primary" size="small" />
                  </CardContent>
                </Card>
              </Grid>

              {/* SMS Option - only shown when SMS MFA is enabled */}
              {smsEnabled && (
                <Grid item xs={12} md={4}>
                  <Card
                    role="button"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleMethodSelect('sms')}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <SmsIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        SMS Text Message
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Receive codes via text message
                      </Typography>
                      <Chip label="Less Secure" color="warning" size="small" />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Why MFA?</strong> Multi-factor authentication adds an extra layer
                  of security to your account, protecting your valuable catalog data from
                  unauthorized access.
                </Typography>
              </Alert>
            </Box>
          </>
        )}

        {activeStep === 1 && selectedMethod === 'totp' && (
          <TOTPSetup
            onComplete={handleSetupComplete}
            onCancel={handleSetupCancel}
          />
        )}

        {activeStep === 1 && selectedMethod === 'sms' && (
          <SMSMFASetup
            onComplete={handleSetupComplete}
            onCancel={handleSetupCancel}
          />
        )}
      </Paper>
    </Container>
  );
};

export default SetupMFA;
